/**
 * Service layer for the Game Master Compendium.
 *
 * Loads every reference catalog a GM needs mid-session (skills, items, effects,
 * modificators, action types, character stats) and normalises them into one
 * searchable entry shape plus cross-reference indexes ("what applies Burning?",
 * "which skills scale off Flow Manipulation?").
 */

import {
    CoreGameApi,
    GameMasterApi,
    ModificatorsGameApi,
    SchoolGameApi,
} from '@/api/backendService.js';
import { isAreaPattern } from '@/utils/spray.js';
import { CharacterActionTypeEnum, EffectEnum } from '@/api/dx-backend';

export const CompendiumKind = {
    SKILL: 'skill',
    ITEM: 'item',
    EFFECT: 'effect',
    MODIFICATOR: 'modificator',
    ACTION: 'action',
    STAT: 'stat',
};

export const KIND_ORDER = [
    { kind: CompendiumKind.SKILL, label: 'Skills', icon: 'fa-wand-magic-sparkles' },
    { kind: CompendiumKind.ITEM, label: 'Items', icon: 'fa-box-open' },
    { kind: CompendiumKind.EFFECT, label: 'Effects', icon: 'fa-biohazard' },
    { kind: CompendiumKind.MODIFICATOR, label: 'Modificators', icon: 'fa-sliders' },
    { kind: CompendiumKind.ACTION, label: 'Actions', icon: 'fa-bolt' },
    { kind: CompendiumKind.STAT, label: 'Stats', icon: 'fa-chart-simple' },
];

const ACTION_NOTES = {
    USE_SKILL: 'Spend a skill from a learned school. Costs are paid up front, impact resolves after the roll.',
    USE_ITEM: 'Consume a charge from an inventory item. Applies the item skill or effect.',
    DIMENSION_SHIFT: 'Move the character between dimensional layers of the same position.',
    CHANGE_POSITION: 'Travel to a connected world position.',
    START_DIALOGUE: 'Open a dialogue screen with a target character.',
    MAKE_DUEL_INVITATION: 'Offer a duel to another character.',
    ACCEPT_DUEL_INVITATION: 'Accept a pending duel and open the fight.',
    REJECT_DUEL_INVITATION: 'Decline a pending duel.',
    START_FIGHT: 'Open a fight at the current position. Aggressive.',
    MOVE: 'Move on the world map.',
    TACTICAL_MOVE: 'Move on the tactical grid during a fight.',
    DICE_ROLL: 'Resolve a challenge with a dice roll.',
    BARGAIN: 'Open a two-sided trade with another character. Offering nothing in return makes it a gift.',
    ANOMALY: 'Interact with a dimensional anomaly.',
    GOD_INTERVENTION: 'Game master only. Apply a direct impact outside the normal rules.',
    LONG_REST: 'Restore attributes over a rest. Blocked in a fight.',
    BACK_TO_SAFE_ZONE: 'Return the character to the last safe position.',
    INSPECT: 'Read a target character or object without acting on it.',
    SNATCH_ITEM: 'Attempt to take an item from a target. Aggressive.',
};

const AGGRESSIVE_ACTIONS = new Set(['START_FIGHT', 'USE_SKILL', 'USE_ITEM', 'SNATCH_ITEM']);
const SPECIAL_ACTIONS = new Set(['LONG_REST', 'BACK_TO_SAFE_ZONE', 'INSPECT', 'SNATCH_ITEM', 'BARGAIN']);
const GM_ACTIONS = new Set(['GOD_INTERVENTION']);

const SCHOOL_HUES = {
    fire: 18,
    water: 205,
    blood: 348,
    energy: 268,
    dark: 255,
    light: 45,
    illusion: 315,
    construct: 210,
    spirit: 165,
    augmentation: 35,
    shield: 140,
    drone: 195,
    'tech-support': 85,
    'tech support': 85,
    medtech: 175,
};

function hueFor(name) {
    if (!name) return null;
    const key = String(name).trim().toLowerCase();
    if (SCHOOL_HUES[key] !== undefined) return SCHOOL_HUES[key];
    for (const [token, hue] of Object.entries(SCHOOL_HUES)) {
        if (key.includes(token)) return hue;
    }
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
        hash = (hash * 31 + key.charCodeAt(i)) % 360;
    }
    return (hash * 137) % 360;
}

export function schoolAccent(name) {
    const hue = hueFor(name);
    if (hue === null) return '#00d9ff';
    return `hsl(${hue} 78% 62%)`;
}

function unwrap(response) {
    const payload = response?.data;
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.results)) return payload.results;
    return [];
}

function asList(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return [value];
}

function titleCase(value) {
    return String(value)
        .replace(/[_-]+/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function statsInFormula(formula) {
    const stats = new Set();
    asList(formula?.requires).forEach((r) => r?.stat && stats.add(r.stat));
    asList(formula?.scaling).forEach((s) => s?.stat && stats.add(s.stat));
    return stats;
}

function skillStats(skill) {
    const stats = new Set();
    asList(skill.impact).forEach((impact) => {
        statsInFormula(impact?.formula).forEach((s) => stats.add(s));
    });
    asList(skill.effect).forEach((effect) => {
        statsInFormula(effect?.duration_modifier?.formula).forEach((s) => stats.add(s));
        asList(effect?.stat_modifiers).forEach((mod) => {
            statsInFormula(mod?.formula).forEach((s) => stats.add(s));
        });
    });
    return [...stats];
}

function pushIndex(index, key, value) {
    if (!key) return;
    const bucket = index.get(key);
    if (bucket) bucket.push(value);
    else index.set(key, [value]);
}

const normalise = (value) => String(value ?? '').trim().toLowerCase();

/**
 * Skill records carry two effect shapes: the AssignableEffect DTO
 * ({base_chance, duration_modifier, stat_modifiers}) and an older fixture shape
 * ({chance, description, durationSeconds}). Read both.
 */
export function effectChance(effect) {
    const raw = effect?.base_chance ?? effect?.chance;
    if (raw == null) return null;
    return raw > 1 ? raw : raw * 100;
}

class GameMasterCompendiumService {
    constructor() {
        this.cache = null;
        this.inFlight = null;
    }

    async load({ force = false } = {}) {
        if (force) {
            this.cache = null;
            this.inFlight = null;
        }
        if (this.cache) return this.cache;
        if (!this.inFlight) {
            this.inFlight = this.fetchAll()
                .then((result) => {
                    this.cache = result;
                    return result;
                })
                .finally(() => {
                    this.inFlight = null;
                });
        }
        return this.inFlight;
    }

    async fetchAll() {
        const [skillsRes, itemsRes, schoolsRes, pathsRes, modsRes, statsRes] = await Promise.allSettled([
            GameMasterApi.gamemasterSkillList(),
            GameMasterApi.gamemasterItemsList(),
            SchoolGameApi.schoolSchoolsGetAllSchoolsRetrieve(),
            SchoolGameApi.schoolPathsGetAllPathsRetrieve(),
            ModificatorsGameApi.modificatorsWorldGetAllModificatorsRetrieve(),
            CoreGameApi.coreCharacterStatsList(),
        ]);

        const failures = [];
        const take = (settled, label) => {
            if (settled.status === 'fulfilled') return unwrap(settled.value);
            failures.push(label);
            console.error(`Compendium: failed to load ${label}`, settled.reason);
            return [];
        };

        const skills = take(skillsRes, 'skills');
        const items = take(itemsRes, 'items');
        const schools = take(schoolsRes, 'schools');
        const paths = take(pathsRes, 'paths');
        const modificators = take(modsRes, 'modificators');
        const stats = take(statsRes, 'stats');

        return this.build({ skills, items, schools, paths, modificators, stats, failures });
    }

    build({ skills, items, schools, paths, modificators, stats, failures }) {
        const schoolById = new Map(schools.map((s) => [String(s.id), s]));
        const pathById = new Map(paths.map((p) => [String(p.id), p]));

        const skillsBySchool = new Map();
        const skillsByEffect = new Map();
        const skillsByStat = new Map();
        const skillsByImpact = new Map();
        const itemsByEffect = new Map();
        const itemsBySkill = new Map();

        const skillEntries = skills.map((skill) => {
            const school = schoolById.get(String(skill.school));
            const schoolName = school?.name || null;
            const impacts = asList(skill.impact);
            const effects = asList(skill.effect);
            const usedStats = skillStats(skill);

            pushIndex(skillsBySchool, String(skill.school), skill);
            effects.forEach((effect) => pushIndex(skillsByEffect, normalise(effect?.name), skill));
            usedStats.forEach((stat) => pushIndex(skillsByStat, stat, skill));
            impacts.forEach((impact) => pushIndex(skillsByImpact, impact?.kind, skill));

            const impactKinds = [...new Set(impacts.map((i) => i?.kind).filter(Boolean))];

            return {
                kind: CompendiumKind.SKILL,
                key: `skill:${skill.id}`,
                id: skill.id,
                name: skill.name,
                icon: skill.icon || null,
                description: skill.description || '',
                group: schoolName || 'Unaffiliated',
                groupId: skill.school ? String(skill.school) : null,
                accent: schoolAccent(schoolName),
                type: skill.type || null,
                typeLabel: skill.type ? titleCase(skill.type) : null,
                tileLabel: skill.type ? titleCase(skill.type) : schoolName || 'Skill',
                grade: skill.grade ?? null,
                tags: impactKinds,
                meta: [
                    skill.type ? titleCase(skill.type) : null,
                    isAreaPattern(skill.spray_config) ? 'Area' : null,
                    skill.immediate ? 'Immediate' : null,
                ].filter(Boolean),
                searchText: [
                    skill.name,
                    skill.description,
                    schoolName,
                    skill.type,
                    skill.special,
                    ...impactKinds,
                    ...effects.map((e) => e?.name),
                    ...usedStats,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase(),
                raw: skill,
                school,
            };
        });

        const itemEntries = items.map((item) => {
            const effectName = item.effect?.id || item.effect?.name || null;
            pushIndex(itemsByEffect, normalise(effectName), item);
            pushIndex(itemsBySkill, item.skill?.id ? String(item.skill.id) : null, item);

            return {
                kind: CompendiumKind.ITEM,
                key: `item:${item.id}`,
                id: item.id,
                name: item.name,
                icon: item.icon || null,
                description: item.description || '',
                group: item.type ? titleCase(item.type) : 'Misc',
                groupId: item.type || 'misc',
                accent: schoolAccent(item.type),
                type: item.type || null,
                typeLabel: item.type ? titleCase(item.type) : null,
                tileLabel: item.type ? titleCase(item.type) : 'Item',
                grade: null,
                tags: [item.skill?.name, effectName].filter(Boolean),
                meta: [
                    item.type ? titleCase(item.type) : null,
                    item.charges ? `${item.charges} charges` : null,
                    item.base_price ? `${item.base_price} cr` : null,
                ].filter(Boolean),
                searchText: [item.name, item.description, item.type, item.skill?.name, effectName]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase(),
                raw: item,
            };
        });

        const canonicalEffects = Object.values(EffectEnum).filter((name) => name !== 'None');
        const canonicalKeys = new Set(canonicalEffects.map(normalise));

        const declaredEffects = new Map();
        skills.forEach((skill) => {
            asList(skill.effect).forEach((effect) => {
                if (!effect?.name) return;
                const key = normalise(effect.name);
                if (canonicalKeys.has(key) || declaredEffects.has(key)) return;
                declaredEffects.set(key, effect.name);
            });
        });
        items.forEach((item) => {
            const name = item.effect?.id || item.effect?.name;
            if (!name) return;
            const key = normalise(name);
            if (canonicalKeys.has(key) || declaredEffects.has(key)) return;
            declaredEffects.set(key, name);
        });

        const effectEntries = [
            ...canonicalEffects.map((name) => ({ name, canonical: true })),
            ...[...declaredEffects.values()].map((name) => ({ name, canonical: false })),
        ].map(({ name, canonical }) => {
            const key = normalise(name);
            const appliedBy = skillsByEffect.get(key) || [];
            const carriedBy = itemsByEffect.get(key) || [];
            const sample = appliedBy
                .flatMap((skill) => asList(skill.effect))
                .find((effect) => normalise(effect?.name) === key);

            const description =
                sample?.description ||
                (sample?.impact
                    ? `Applies ${sample.impact.kind} (${sample.impact.type}) while active.`
                    : canonical
                        ? 'Engine status effect. Sources below define its chance, duration and stat changes.'
                        : 'Declared by skill data. Not one of the engine status effects.');

            return {
                kind: CompendiumKind.EFFECT,
                key: `effect:${key}`,
                id: name,
                name,
                icon: null,
                description,
                group: canonical ? 'Engine effects' : 'Skill-declared',
                groupId: canonical ? 'engine' : 'declared',
                accent: schoolAccent(name),
                type: canonical ? 'engine' : 'declared',
                typeLabel: canonical ? 'Engine' : 'Declared',
                tileLabel: `${appliedBy.length + carriedBy.length} ${appliedBy.length + carriedBy.length === 1 ? 'source' : 'sources'}`,
                grade: null,
                tags: [],
                meta: [
                    `${appliedBy.length} ${appliedBy.length === 1 ? 'skill' : 'skills'}`,
                    `${carriedBy.length} ${carriedBy.length === 1 ? 'item' : 'items'}`,
                ],
                searchText: [name, ...appliedBy.map((s) => s.name), ...carriedBy.map((i) => i.name)]
                    .join(' ')
                    .toLowerCase(),
                raw: { name, canonical, sample, appliedBy, carriedBy },
            };
        });

        const modificatorEntries = modificators.map((mod) => ({
            kind: CompendiumKind.MODIFICATOR,
            key: `mod:${mod.id}`,
            id: mod.id,
            name: mod.name,
            icon: mod.icon || null,
            description: mod.description || '',
            group: 'Modificators',
            groupId: 'modificators',
            accent: schoolAccent(mod.name),
            type: null,
            typeLabel: null,
            tileLabel: 'Modificator',
            grade: null,
            tags: [],
            meta: [mod.stat_changes ? String(mod.stat_changes) : null].filter(Boolean),
            searchText: [mod.name, mod.description, mod.stat_changes].filter(Boolean).join(' ').toLowerCase(),
            raw: mod,
        }));

        const actionEntries = Object.values(CharacterActionTypeEnum).map((value) => {
            const audience = GM_ACTIONS.has(value)
                ? 'Game master'
                : SPECIAL_ACTIONS.has(value)
                    ? 'Special'
                    : 'Standard';
            return {
                kind: CompendiumKind.ACTION,
                key: `action:${value}`,
                id: value,
                name: titleCase(value),
                icon: null,
                description: ACTION_NOTES[value] || 'Action type accepted by the action pipeline.',
                group: audience,
                groupId: audience.toLowerCase().replace(/\s+/g, '-'),
                accent: schoolAccent(value),
                type: audience,
                typeLabel: audience,
                tileLabel: audience,
                grade: null,
                tags: AGGRESSIVE_ACTIONS.has(value) ? ['Aggressive'] : [],
                meta: [value],
                searchText: [value, titleCase(value), ACTION_NOTES[value], audience]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase(),
                raw: {
                    value,
                    audience,
                    aggressive: AGGRESSIVE_ACTIONS.has(value),
                    special: SPECIAL_ACTIONS.has(value),
                },
            };
        });

        const statEntries = stats.map((stat) => {
            const usedBy = skillsByStat.get(stat.name) || [];
            return {
                kind: CompendiumKind.STAT,
                key: `stat:${stat.name}`,
                id: stat.name,
                name: stat.name,
                icon: null,
                description: stat.description || '',
                group: 'Character stats',
                groupId: 'stats',
                accent: schoolAccent(stat.name),
                type: null,
                typeLabel: null,
                tileLabel: `${usedBy.length} ${usedBy.length === 1 ? 'skill' : 'skills'}`,
                grade: null,
                tags: [],
                meta: [`${usedBy.length} ${usedBy.length === 1 ? 'skill' : 'skills'}`],
                searchText: [stat.name, stat.description, ...usedBy.map((s) => s.name)]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase(),
                raw: { stat, usedBy },
            };
        });

        const entries = [
            ...skillEntries,
            ...itemEntries,
            ...effectEntries,
            ...modificatorEntries,
            ...actionEntries,
            ...statEntries,
        ];

        return {
            entries,
            byKey: new Map(entries.map((e) => [e.key, e])),
            schools: schools
                .map((school) => ({
                    ...school,
                    accent: schoolAccent(school.name),
                    pathNames: asList(school.path)
                        .map((id) => pathById.get(String(id))?.name)
                        .filter(Boolean),
                    skillCount: (skillsBySchool.get(String(school.id)) || []).length,
                }))
                .sort((a, b) => a.name.localeCompare(b.name)),
            paths,
            indexes: { skillsBySchool, skillsByEffect, skillsByStat, skillsByImpact, itemsByEffect, itemsBySkill },
            failures,
        };
    }
}

export const gameMasterCompendiumService = new GameMasterCompendiumService();
export default gameMasterCompendiumService;
